import React, { useEffect, useState } from "react"
import styled from "@emotion/styled"
import moment from "moment";
import EditIcon from '@mui/icons-material/Edit'
import UndoIcon from '@mui/icons-material/Undo'
import { useTheme } from "@mui/styles"
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { useDispatch } from "react-redux"
import EditSwitchboard from "./EditSwitchboard/EditSwitchboard"
import CircularProgress from "@mui/material/CircularProgress"
import { setShowLeadActionsModal } from "../../../../../slices/sessionDataSlice"
import { AccentInfo, CompareDetail, CompareLink, ListRow, DetailWrapper } from "../../../../../StyledComponents"
import { useEditOneMortgageDetailMutation, useResolveOneMortgageDetailMutation, useUndoOneMortgageDetailMutation } from "../../../../../slices/api/apiSlice"

const RecordRow = ({ recordFieldRoot, recordField, leadData, valueType, handleWorkFlow, userId, userFullName, record, mortgageId, workflowStage, fieldEditing, activeDiscrepancies, resolvedDiscrepancies, rejectedDiscrepancies, location, editingRecords, dateFormat, teamId }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [editOneMortgageDetail] = useEditOneMortgageDetailMutation()
  const [undoOneMortgageDetail] = useUndoOneMortgageDetailMutation()
  const [resolveOneMortgageDetail] = useResolveOneMortgageDetailMutation()

  const [rowStatus, setRowStatus] = useState(null)
  const [editIsFetching, setEditIsFetching] = useState(false)

  const handleSubmitEdit = async (selection, customEditValue, dateFormat) => {
    setEditIsFetching(true)
    let leadId = null
    let leadStatus = null
    if (leadData) {
      leadId = leadData._id
      leadStatus = leadData.status
    }
    let reqObj = {
      userId: userId,
      userFullName: userFullName,
      mortgageId: mortgageId,
      leadId: leadId,
      recordField: recordField,
      customValue: customEditValue,
      activeDiscrepancies: activeDiscrepancies,
      resolvedDiscrepancies: resolvedDiscrepancies,
      rejectedDiscrepancies: rejectedDiscrepancies,
      publicRecordValue: record.publicRecordValue,
      oldDiscrepancy: record.discrepancy,
      recordFieldRoot: recordFieldRoot,
      dateFormat: dateFormat,
      discardLead: false,
      teamId: teamId,
      selection: null,
      financialsUpdated: false,
      leadStatus: leadStatus,
      origin: 'compareRecords',
    }
    if (recordField === 'PrimaryMortgageAmount' || recordField === 'PrimaryMortgageInterestRate' || recordField === 'PrimaryMortgageStartDate' || recordField === 'PrimaryMortgageTerm') {
      reqObj.financialsUpdated = true
    }
    let resp = {}
    if (selection === 'undo') {
      // if (rejectedDiscrepancies === 1 && (leadData && leadData.tier === 5) && record.discrepancy !== 'provided') {
      //   reqObj.discardLead = true
      // }
      resp = await undoOneMortgageDetail(reqObj)
    } else if (selection === 'edit') {
      resp = await editOneMortgageDetail(reqObj)
    } else if (selection === 'resolve') {
      resp = await resolveOneMortgageDetail(reqObj)
    } else if (selection === 'keep') {
      reqObj.customValue = record.currentValue
      reqObj.selection = 'keep'
      resp = await editOneMortgageDetail(reqObj)
    }
    if (resp.error) {
      setRowStatus('error')
      handleWorkFlow('', '')
    } else {
      if (selection === 'undo') {
        if (rejectedDiscrepancies === 2 && (leadData && leadData.tier === 5) && location === 'activeLeads') {
          dispatch( setShowLeadActionsModal(false) )
        }
        setRowStatus(null)
      } else {
        setRowStatus('success')
      }
      handleWorkFlow('', '')
    }
    setEditIsFetching(false)
  }

  const [publicRecordValue, setPublicRecordValue] = useState(null)
  const [internalRecordValue, setInternalRecordValue] = useState(null)

  useEffect(() => {
    if (valueType === 'date') {
      if (record.publicRecordValue !== null) {
        if (record.publicRecordValue.length > 15) {
          let newPRDate = moment(record.publicRecordValue).format("MMM Do, YYYY")
          setPublicRecordValue(newPRDate)
        } else {
          setPublicRecordValue(record.publicRecordValue)
        }
      }
      if (record.currentValue !== null) {
        if (record.currentValue.length > 15) {
          let newIRDate = moment(record.currentValue).format("MMM Do, YYYY")
          setInternalRecordValue(newIRDate)
        } else {
          setInternalRecordValue(record.currentValue)
        }
      }
    } else {
      setPublicRecordValue(record.publicRecordValue)
      setInternalRecordValue(record.currentValue)
    }
  }, [record])

  return (
    <RecordListRow className={(record.discrepancy === 'active' || record.discrepancy === 'rejected' ? 'discrepancy ' : '') + (fieldEditing === recordField ? 'compareRecordsOpen' : rowStatus === 'error' ? 'error compareRecordsClosed' : rowStatus === 'success' ? 'success compareRecordsClosed' : 'compareRecordsClosed')}>
      <CompareDetail className='recordRow fieldCell' style={{gridColumn: '2'}}>
        <DetailWrapper className={`fieldPill ${record.discrepancy === 'provided' ? 'provided' : (record.discrepancy === 'active' || record.discrepancy === 'rejected') ? `${record.assignedTier}` : ''}`}>
          {record.label}
        </DetailWrapper>
      </CompareDetail>
      {publicRecordValue ?
        <CompareDetail className="recordRow valueCell publicValue" style={{gridColumn: '3'}}>
          {record.label.includes('Interest') ?
            <>
              {parseFloat(publicRecordValue).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 2})}%
            </>
          : record.label === 'Assessed Value' || record.label === 'Land Value' || record.label === 'Improvements Value' || record.label === 'Close Price' || record.label === 'Assessed Land Value' || record.label === 'Assessed Improvement Value' || record.label === 'Primary Mortgage Amount' ?
            <>
              ${parseFloat(publicRecordValue).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}
            </>
          : record.label === 'Tax Annual Amount' ?
            <>
              ${parseFloat(publicRecordValue).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 2})}
            </>
          : record.label === 'Latitude' || record.label === 'Longitude' || record.label === 'Rooms' || record.label === 'Bedrooms' || record.label === 'Bathrooms Full' || record.label === 'Bathrooms Total' ?
            <>
              {parseFloat(publicRecordValue)}
            </>
          : record.label === 'Living Area' || record.label === 'Garage Area' || record.label === 'Building Area Total' || record.label === 'Gross Area' || record.label === 'Lot Size Square Feet' ?
            <>
              {parseFloat(publicRecordValue).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})} sq ft
            </>
          :
            publicRecordValue
          }
        </CompareDetail>
      :
        <CompareDetail className="recordRow valueCell publicValue emptyValue" style={{gridColumn: '3'}}>
          <AccentInfo>-empty-</AccentInfo>
        </CompareDetail>
      }
      <CompareDetail className="recordRow valueCell internalValue" style={{gridColumn: '4'}}>
        {internalRecordValue ?
          <DetailWrapper className={`valuePill ${record.discrepancy === 'active' ? "active" : record.discrepancy === 'rejected' ? "rejected" : record.discrepancy === 'resolved' ? 'resolved' : record.discrepancy === 'inactive' ? 'inactive' : ''}`}>
            {record.label.includes('Interest') ?
              <>
                {parseFloat(internalRecordValue).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 2})}%
              </>
            : record.label === 'Assessed Value' || record.label === 'Land Value' || record.label === 'Improvements Value' || record.label === 'Close Price' || record.label === 'Assessed Land Value' || record.label === 'Assessed Improvement Value' || record.label === 'Primary Mortgage Amount' ?
              <>
                ${parseFloat(internalRecordValue).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}
              </>
            : record.label === 'Tax Annual Amount' ?
              <>
                ${parseFloat(internalRecordValue).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 2})}
              </>
            : record.label === 'Latitude' || record.label === 'Longitude' || record.label === 'Rooms' || record.label === 'Bedrooms' || record.label === 'Bathrooms Full' || record.label === 'Bathrooms Total' ?
              <>
                {parseFloat(internalRecordValue)}
              </>
            : record.label === 'Living Area' || record.label === 'Garage Area' || record.label === 'Building Area Total' || record.label === 'Gross Area' || record.label === 'Lot Size Square Feet' ?
              <>
                {parseFloat(internalRecordValue).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})} sq ft
              </>
            :
              internalRecordValue
            }
          </DetailWrapper>
        :
          <DetailWrapper className={`valuePill ${record.discrepancy === 'active' ? "active fine empty" : record.discrepancy === 'rejected' ? "rejected fine empty" : record.discrepancy === 'resolved' ? 'resolved fine empty' : record.discrepancy === 'inactive' ? 'gray fine empty' : ''}`}>
            -empty-
          </DetailWrapper>
        }
      </CompareDetail>
      {editIsFetching ?
        <CompareDetail style={{gridColumn: '4', gridRow: '2', justifyContent: 'center', height: '10rem'}}>
          <CircularProgress 
            sx={{ color: `${theme.palette.primary.main}`, margin: 'auto' }}
            size={24}
          /> 
        </CompareDetail>
      : fieldEditing === '' && editingRecords ?
      // : fieldEditing === '' && (editingRecords || record.status !== 'inactive') ?
        <CompareDetail 
          className='recordRow actionCell'
          style={{gridColumn: '5'}}
          onClick={
            record.status === 'discrepancy' ? () => handleWorkFlow('discrepancy', recordField) 
            : record.status === 'edited' || record.status === 'provided' ? () => handleWorkFlow('undo', recordField)
            // : (record.status === 'edited' || record.status === 'inactive' || record.status === 'initial') ? () => handleWorkFlow('undo', recordField)
            : () => handleWorkFlow('custom', recordField)
          }
        >
          <CompareLink 
            className="gray small rowAction"
          >
            {record.status === 'discrepancy' ?
              `resolve`
            : record.status === 'edited' || record.status === 'provided' ?
              <UndoIcon style={{height: '1.2rem', width: '1.2rem'}} />
            : valueType === 'boolean' ?
              <SwapHorizIcon style={{height: '1.2rem', width: '1.2rem'}} />
            :
              <EditIcon style={{height: '1.2rem', width: '1.2rem'}} />
            }
          </CompareLink>
        </CompareDetail>
      : (editingRecords || record.status !== 'inactive') && fieldEditing === recordField ?
        <EditSwitchboard recordField={recordField} workflowStage={workflowStage} handleWorkFlow={handleWorkFlow} handleSubmitEdit={handleSubmitEdit} valueType={valueType} record={record} editIsFetching={editIsFetching} dateFormat={dateFormat} rejectedDiscrepancies={rejectedDiscrepancies} leadData={leadData} />
      :
        <CompareDetail 
          className='recordRow actionCell'
          style={{gridColumn: '5'}}
        />
      }
    </RecordListRow>
  )
}

const RecordListRow = styled(ListRow)(({ theme }) => `
  &.compareRecordsOpen,
  &.compareRecordsClosed {
    grid-template-columns: 0rem 20rem minmax(0, 1fr) minmax(0, 1fr) 5rem;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
    background: ${theme.palette.common.white};
    position: relative;
    overflow: hidden;
  }

  &.compareRecordsOpen {
    grid-template-rows: 2.6rem 12rem;
  }

  &.compareRecordsClosed {
    grid-template-rows: 2.6rem 0;
  }

  &.discrepancy {
    background: ${theme.component.row.discrepancyHighlight};
  }

  .recordRow {
    margin: 0;
    padding: 0.25rem 0.7rem;
    border-top: 0;
    min-height: 0;
  }

  .fieldCell {
    justify-content: flex-end;
    border-right: 0.1rem solid ${theme.palette.common.grayBorder};
    background: rgba(248, 250, 252, 0.72);
  }

  .valueCell {
    justify-content: center;
    color: ${theme.palette.common.black};
    font-size: 0.95rem;
    line-height: 1.25;
    overflow: hidden;
  }

  .internalValue {
    border-left: 0.1rem solid ${theme.palette.common.grayBorder};
  }

  .actionCell {
    justify-content: center;
    padding-right: 0.6rem;
  }

  .fieldPill {
    max-width: 100%;
    border-radius: 0.6rem;
    padding: 0.18rem 0.6rem;
    line-height: 1.2;
    white-space: normal;
    text-align: right;
  }

  .valuePill {
    max-width: 100%;
    border-radius: 0.6rem;
    padding: 0.18rem 0.6rem;
    line-height: 1.25;
    white-space: normal;
    word-break: break-word;
  }

  .empty {
    font-weight: 400;
  }

  .emptyValue {
    color: ${theme.palette.common.grayText};
  }

  .rowAction {
    width: 100%;
    max-width: 4.8rem;
    min-height: 1.8rem;
    margin: 0;
    border-radius: 0.6rem;
    font-size: 0.85rem;
    text-transform: capitalize;
  }

  .rowAction svg {
    width: 1.1rem;
    height: 1.1rem;
  }
`)

export default RecordRow
